import { router } from "@inertiajs/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import type { PagyMetadata } from "@/types";

interface AccountsPaginationProps {
  pagy: PagyMetadata;
}

export function AccountsPagination({ pagy }: AccountsPaginationProps) {
  if (pagy.last <= 1) {
    return null;
  }

  const navigate = (page: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(page));
    router.get(
      url.pathname + url.search,
      {},
      {
        preserveState: true,
        preserveScroll: false,
      },
    );
  };

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const total = pagy.last;
    const current = pagy.page;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (current > 3) {
        pages.push("ellipsis");
      }

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (current < total - 2) {
        pages.push("ellipsis");
      }

      pages.push(total);
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-between pt-4">
      <div className="text-sm text-muted-foreground">
        Showing {pagy.from} to {pagy.to} of {pagy.count} accounts
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => navigate(pagy.previous!)}
          disabled={!pagy.previous}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {getPageNumbers().map((page, index) =>
          page === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="flex items-center justify-center w-8 h-8 text-muted-foreground"
            >
              ...
            </span>
          ) : (
            <Button
              key={page}
              variant={page === pagy.page ? "default" : "ghost"}
              size="icon-sm"
              onClick={() => navigate(page)}
              className="min-w-8"
            >
              {page}
            </Button>
          ),
        )}

        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => navigate(pagy.next!)}
          disabled={!pagy.next}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
